import type { SelectChangeEvent } from '@mui/material'
import {
  Box,
  CircularProgress,
  FormControl,
  Grid2 as Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { FilePdfOutlined } from '@ant-design/icons'
import Paper from 'components/@extended/Paper'
import EmployeeRewardsContent from 'components/EmployeeRewardsContent'
import SimpleTitle from 'components/SimpleTitle'
import { useSetBreadcrumbActions } from 'contexts/BreadcrumbActionsContext'
import useAuth from 'hooks/useAuth'
import { useFetchOrganization } from 'hooks/useOrganizations'
import { usePeriods } from 'hooks/usePeriods'
import { useFetchAllUserRewardReports } from 'hooks/useUserRewards'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getRootOrganizationId } from 'types/user-profile'
import { findPeriodById } from 'types/periods'
import { analytics } from 'utils/analytics'
import { useCreateEngagement } from 'hooks/useEngagements'

interface EmployeeRewardsDashboardProps {
  userId: string
}

const RewardsDashboard: React.FC<EmployeeRewardsDashboardProps> = ({ userId }) => {
  const theme = useTheme()
  const { token, profile } = useAuth()
  const setBreadcrumbActions = useSetBreadcrumbActions()

  const [rootOrganizationId, setRootOrganizationId] = useState('')
  const [selectedReportIndex, setSelectedReportIndex] = useState<number>(0)
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)

  // Get all reward reports for user
  const { reports, isLoading: reportsLoading } = useFetchAllUserRewardReports(
    userId || '',
    rootOrganizationId,
    token!,
    !!userId && !!rootOrganizationId && !!token
  )

  // Get periods for labels
  const { periods: periodsResponse } = usePeriods(token!, rootOrganizationId, !!rootOrganizationId && !!token)
  const periodsData = useMemo(() => periodsResponse?.periods || [], [periodsResponse])

  // Get organization for logo
  const { organization } = useFetchOrganization(rootOrganizationId, token!)

  const { createEngagement } = useCreateEngagement(token!)

  // Get selected report
  const selectedReport = useMemo(() => {
    if (!reports || reports.length === 0) return null
    return reports[selectedReportIndex] || reports[0]
  }, [reports, selectedReportIndex])

  // Helper to get period label from periodId
  const getPeriodLabel = useCallback(
    (periodId: string) => {
      if (!periodsData.length) return periodId
      const period = findPeriodById(periodsData, periodId)
      return period?.label || periodId
    },
    [periodsData]
  )

  // Get period label from selected report
  const selectedPeriodLabel = useMemo(() => {
    if (!selectedReport?.periodId) return ''
    return getPeriodLabel(selectedReport.periodId)
  }, [selectedReport, getPeriodLabel])

  // Get user name
  const userName = useMemo(() => {
    if (selectedReport?.firstName && selectedReport?.lastName) {
      return `${selectedReport.firstName} ${selectedReport.lastName}`
    }
    return 'Employee'
  }, [selectedReport?.firstName, selectedReport?.lastName])

  useEffect(() => {
    if (profile) {
      const orgId = getRootOrganizationId(profile)
      if (orgId) {
        setRootOrganizationId(orgId)
      }
    }
  }, [profile])

  const handleReportChange = useCallback((event: SelectChangeEvent<string>) => {
    setSelectedReportIndex(Number(event.target.value))
  }, [])

  const handleDownloadPdf = useCallback(async () => {
    if (!selectedReport?.id || !selectedReport?.revision) {
      console.error('No report ID or revision available')
      return
    }

    setIsGeneratingPdf(true)
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_SERVER_URL}rewards/user-rewards/${selectedReport.id}/${selectedReport.revision}/pdf`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )

      if (!response.ok) {
        throw new Error('Failed to generate PDF')
      }

      const data = await response.json()

      // Open the presigned URL - S3 response-content-disposition header will trigger download
      window.open(data.url, '_blank')
    } catch (error) {
      console.error('Failed to generate PDF:', error)
    } finally {
      setIsGeneratingPdf(false)
    }
  }, [selectedReport?.id, selectedReport?.revision, token])

  // Show loading while waiting for required data
  const isLoading = !rootOrganizationId || reportsLoading

  // Check if user has no rewards
  const hasNoRewards =
    !reports || reports.length === 0 || !selectedReport?.categories || selectedReport.categories.length === 0

  // Memoize breadcrumb actions to prevent infinite re-renders
  const breadcrumbActions = useMemo(() => {
    if (isLoading || hasNoRewards) return null

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {/* PDF Download Button */}
        <Tooltip title="Download as PDF">
          <IconButton
            onClick={handleDownloadPdf}
            disabled={isGeneratingPdf || !selectedReport?.id || !selectedReport?.revision}
            sx={{
              color: theme.palette.text.secondary,
              '&:hover': {
                color: theme.palette.primary.main,
              },
            }}
          >
            {isGeneratingPdf ? <CircularProgress size={20} color="inherit" /> : <FilePdfOutlined />}
          </IconButton>
        </Tooltip>
      </Box>
    )
  }, [isLoading, hasNoRewards, isGeneratingPdf, theme, handleDownloadPdf, selectedReport?.id, selectedReport?.revision])

  // Set breadcrumb actions (must be before early returns)
  useEffect(() => {
    setBreadcrumbActions(breadcrumbActions)

    // Clear breadcrumb actions on unmount
    return () => {
      setBreadcrumbActions(null)
    }
  }, [breadcrumbActions, setBreadcrumbActions])

  // Track rewards dashboard view
  useEffect(() => {
    if (!isLoading && !hasNoRewards && selectedReport) {
      if (profile) {
        const rootOrgId = getRootOrganizationId(profile)
        createEngagement({
          userId: profile?.user.id,
          eventType: 'rewards_dashboard_viewed',
          rootOrganizationId: rootOrgId,
          viewedRecordId: selectedReport.id,
          viewedRecordUserId: userId,
          viewedRecordType: 'user_rewards',
        })
      }
      analytics.track('rewards_dashboard_viewed', {
        userId: profile?.user.id,
        viewedForUserId: userId,
      })
    }
  }, [isLoading, hasNoRewards, selectedReport])

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  if (hasNoRewards) {
    return (
      <>
        <Grid container pb={2} spacing={2}>
          {/* Title */}
          <Grid size={{ xs: 12, md: reports && reports.length > 1 ? 10 : 12 }}>
            <SimpleTitle title="Total Rewards" />
          </Grid>

          {/* Report Selector - only shown if multiple reports */}
          {reports && reports.length > 1 && (
            <Grid
              size={{ xs: 12, md: 2 }}
              sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, mt: { xs: 0, md: 1 } }}
            >
              <FormControl variant="outlined" fullWidth>
                <InputLabel
                  shrink
                  variant="outlined"
                  sx={{
                    '&.MuiInputLabel-shrink': {
                      background: theme.palette.background.default,
                    },
                  }}
                >
                  Period
                </InputLabel>
                <Select
                  labelId="report-select-label"
                  id="report-select"
                  variant="outlined"
                  value={String(selectedReportIndex)}
                  label="Period"
                  style={{ borderRadius: '21px' }}
                  onChange={handleReportChange}
                >
                  {reports.map((report, index) => (
                    <MenuItem key={report.periodId} value={String(index)}>
                      {getPeriodLabel(report.periodId)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Paper elevation={0}>
            <Typography variant="h5">No rewards package found</Typography>
            <Typography variant="body1">
              You do not have a rewards package assigned for this period. Please contact your manager or HR department
              for assistance.
            </Typography>
          </Paper>
        </Grid>
      </>
    )
  }

  return (
    <>
      <Grid container pb={2} spacing={2}>
        {/* Title */}
        <Grid size={{ xs: 12, md: reports && reports.length > 1 ? 10 : 12 }}>
          <SimpleTitle title="Total Rewards" />
        </Grid>

        {/* Report Selector - only shown if multiple reports */}
        {reports && reports.length > 1 && (
          <Grid
            size={{ xs: 12, md: 2 }}
            sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, mt: { xs: 0, md: 1 } }}
          >
            <FormControl variant="outlined" fullWidth>
              <InputLabel
                shrink
                variant="outlined"
                sx={{
                  '&.MuiInputLabel-shrink': {
                    background: theme.palette.background.default,
                  },
                }}
              >
                Period
              </InputLabel>
              <Select
                labelId="report-select-label"
                id="report-select"
                variant="outlined"
                value={String(selectedReportIndex)}
                label="Period"
                style={{ borderRadius: '21px' }}
                onChange={handleReportChange}
              >
                {reports.map((report, index) => (
                  <MenuItem key={report.periodId} value={String(index)}>
                    {getPeriodLabel(report.periodId)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}
      </Grid>

      <EmployeeRewardsContent
        report={selectedReport}
        userName={userName}
        userId={userId || ''}
        periodLabel={selectedPeriodLabel}
        contentRef={contentRef}
        description={selectedReport?.package?.description}
        disclaimer={selectedReport?.package?.disclaimer}
        logoUrl={organization?.metadata?.logoBase64 || organization?.metadata?.logoUrl}
      />
    </>
  )
}

export default RewardsDashboard
